import React from "react";

import ErrorPage from "../../pages/ErrorPage";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0,
      errorId: null
    };
    
    // Bind methods
    this.handleUnhandledError = this.handleUnhandledError.bind(this);
    this.handleUnhandledRejection = this.handleUnhandledRejection.bind(this);
  }

  componentDidMount() {
    // Listen for unhandled JavaScript errors
    window.addEventListener('error', this.handleUnhandledError);
    
    // Listen for unhandled promise rejections
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    // Clean up event listeners
    window.removeEventListener('error', this.handleUnhandledError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  handleUnhandledError(event) {
    console.error('Unhandled JavaScript error:', event.error);
    
    // Log the error details
    this.logError(event.error, {
      type: 'unhandled_error',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack
    });

    // Set error state
    this.setState({
      hasError: true,
      error: event.error,
      errorInfo: {
        type: 'unhandled_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      },
      errorId: this.generateErrorId()
    });
  }

  handleUnhandledRejection(event) {
    console.error('Unhandled promise rejection:', event.reason);
    
    // Log the rejection details
    this.logError(event.reason, {
      type: 'unhandled_rejection',
      reason: event.reason
    });

    // Set error state
    this.setState({
      hasError: true,
      error: event.reason,
      errorInfo: {
        type: 'unhandled_rejection',
        reason: event.reason
      },
      errorId: this.generateErrorId()
    });

    // Prevent the default unhandled rejection behavior
    event.preventDefault();
  }

  generateErrorId() {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  logError(error, errorInfo) {
    const errorDetails = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      error: {
        message: error?.message || String(error),
        stack: error?.stack,
        name: error?.name
      },
      errorInfo,
      userId: this.getUserId(),
      sessionId: this.getSessionId()
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Error Boundary - Error Details');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Full Details:', errorDetails);
      console.groupEnd();
    }

    // In production, you would send this to your error tracking service
    if (process.env.NODE_ENV === 'production') {
      // Example integrations:
      // Sentry.captureException(error, { extra: errorDetails });
      // LogRocket.captureException(error);
      // Bugsnag.notify(error, errorDetails);
      
      // For now, we'll just log to console
      console.error('Production Error:', errorDetails);
    }
  }

  getUserId() {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.id || 'anonymous';
    } catch {
      return 'anonymous';
    }
  }

  getSessionId() {
    try {
      let sessionId = sessionStorage.getItem('sessionId');
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('sessionId', sessionId);
      }
      return sessionId;
    } catch {
      return 'unknown';
    }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught a React error:", error, errorInfo);
    
    const errorId = this.generateErrorId();
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
      errorId: errorId
    });

    // Log the error with detailed information
    this.logError(error, {
      type: 'react_error',
      componentStack: errorInfo.componentStack,
      errorBoundary: this.constructor.name,
      errorInfo: errorInfo
    });
  }

  handleRetry = () => {
    const newRetryCount = this.state.retryCount + 1;
    
    // Limit retries to prevent infinite loops
    if (newRetryCount > 3) {
      console.warn('Max retry attempts reached. Reloading page...');
      window.location.reload();
      return;
    }

    console.log(`Retrying... Attempt ${newRetryCount}`);
    
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: newRetryCount,
      errorId: null
    });
  };

  handleReload = () => {
    // Clear any stored error state before reload
    try {
      sessionStorage.removeItem('lastError');
      localStorage.removeItem('errorCount');
    } catch (e) {
      console.warn('Could not clear error state:', e);
    }
    
    window.location.reload();
  };

  handleReport = () => {
    const { error, errorInfo, errorId } = this.state;
    
    // Create a detailed error report
    const errorReport = {
      errorId,
      timestamp: new Date().toISOString(),
      error: {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      },
      errorInfo,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getUserId(),
      retryCount: this.state.retryCount
    };

    // In a real app, you would send this to your error reporting service
    console.log('Error Report:', errorReport);
    
    // For now, we'll copy to clipboard for manual reporting
    if (navigator.clipboard) {
      navigator.clipboard.writeText(JSON.stringify(errorReport, null, 2))
        .then(() => alert('Error report copied to clipboard'))
        .catch(() => console.warn('Could not copy error report'));
    } else {
      alert('Error ID: ' + errorId + '\nCheck console for details');
    }
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorId, retryCount } = this.state;
      
      return (
        <ErrorPage 
          errorCode="500" 
          error={error}
          errorInfo={errorInfo}
          errorId={errorId}
          retryCount={retryCount}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
          onReport={this.handleReport}
          maxRetries={3}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
